import { useEffect, useState } from "react";
import { useUser, useClerk } from "@clerk/react";
import { AmplifilesClient } from "@studio/lib/api/api-client";
import { Outlet, Navigate } from "react-router";

export default function AdminAuthGuard() {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isLoaded || !user) {
        setIsChecking(false);
        return;
      }

      try {
        // Try to call an admin endpoint to check if user has admin rights
        // This will fail with 403 if user is not an admin
        await AmplifilesClient.instance.get("/user/self");

        // For now, we'll check if the user email contains 'amplifiles' as a simple admin check
        // TODO: Replace with proper role checking from backend
        const isAdminUser =
          user.primaryEmailAddress?.emailAddress?.endsWith("@amplifiles.ai") ||
          false;

        setIsAdmin(isAdminUser);
      } catch (error) {
        console.error("Admin check failed:", error);
        setIsAdmin(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAdminStatus();
  }, [user, isLoaded]);

  // Handle logout for non-admin users
  useEffect(() => {
    if (!isLoaded || isChecking) return;

    if (user && isAdmin === false && !isSigningOut) {
      // Sign out non-admin users before redirecting
      setIsSigningOut(true);
      clerk.signOut();
    }
  }, [isAdmin, user, isLoaded, isChecking, clerk, isSigningOut]);

  if (!isLoaded || isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (isSigningOut) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-red-600">No admin access. Signing out...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/sign-in" replace />;
  }

  return <Outlet />;
}
